<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  dateFormat,
  shortTimestamp,
} from 'shared/helpers/timeHelper';

export default {
  name: 'EnhancedTimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    lastClientMessageTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      lastClientMessageTimeAgo: dynamicTime(this.lastClientMessageTimestamp),
      timer: null,
    };
  },
  computed: {
    lastActivityTime() {
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    lastClientMessageTime() {
      return shortTimestamp(this.lastClientMessageTimeAgo);
    },
    lastClientMessageActualTime() {
      // Use chat's last activity timestamp as the source of truth for the main time field
      if (!this.lastActivityTimestamp) return '';

      // Normalize timestamp to milliseconds (supports seconds, ms, Date, or ISO string)
      const ts =
        // eslint-disable-next-line no-nested-ternary
        typeof this.lastActivityTimestamp === 'number' ||
        typeof this.lastActivityTimestamp === 'string'
          ? Number(this.lastActivityTimestamp) *
            (String(this.lastActivityTimestamp).length === 10 ? 1000 : 1)
          : this.lastActivityTimestamp instanceof Date
            ? this.lastActivityTimestamp.getTime()
            : Date.parse(this.lastActivityTimestamp);

      const date = new Date(ts);
      const now = new Date();

      const sameDay =
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate();

      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

      const timePart = date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      if (sameDay) {
        // Today -> "10:35"
        return timePart;
      }

      if (diffDays < 7) {
        // Within a week -> "Mon 10:35"
        const weekday = date.toLocaleDateString(undefined, {
          weekday: 'short',
        });
        return `${weekday} ${timePart}`;
      }

      // Older -> use existing dateFormat helper (keeps app consistency)
      return dateFormat(this.lastActivityTimestamp);
    },
    createdAt() {
      const createdTimeDiff = Date.now() - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgo
          }`
        : `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST')} ${dateFormat(
            this.createdAtTimestamp
          )}`;
    },
    lastActivity() {
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            this.lastActivityAtTimeAgo
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormat(this.lastActivityTimestamp)}`;
    },
    lastClientMessage() {
      if (!this.lastClientMessageTimestamp) return '';
      const lastClientMessageTimeDiff =
        Date.now() - this.lastClientMessageTimestamp * 1000;
      const isNotActive = lastClientMessageTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_CLIENT_MESSAGE.ACTIVE')} ${
            this.lastClientMessageTimeAgo
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_CLIENT_MESSAGE.NOT_ACTIVE'
          )} ${dateFormat(this.lastClientMessageTimestamp)}`;
    },
    tooltipText() {
      return `${this.createdAt}
              ${this.lastActivity}
              ${this.lastClientMessage}`;
    },
    waitTimeInMinutes() {
      if (!this.lastClientMessageTimestamp) return 0;

      // Check if the last message is from the client (incoming)
      // If the last activity is from an agent (outgoing), customer is not waiting
      const lastActivityTime = this.lastActivityTimestamp * 1000;
      const lastClientMessageTime = this.lastClientMessageTimestamp * 1000;

      // If last activity is newer than last client message, customer is not waiting
      if (lastActivityTime > lastClientMessageTime) {
        return 0;
      }

      // Customer is waiting - calculate time since their last message
      const timeDiff = Date.now() - lastClientMessageTime;
      return Math.floor(timeDiff / MINUTE_IN_MILLI_SECONDS);
    },
    colorClass() {
      const minutes = this.waitTimeInMinutes;
      if (minutes <= 15) return '';
      if (minutes <= 30) return 'text-yellow-600 dark:text-yellow-400';
      if (minutes <= 60) return 'text-red-600 dark:text-red-400';
      return 'text-amber-800 dark:text-amber-600';
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
    },
    lastClientMessageTimestamp() {
      this.lastClientMessageTimeAgo = dynamicTime(
        this.lastClientMessageTimestamp
      );
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.lastClientMessageTimeAgo = dynamicTime(
          this.lastClientMessageTimestamp
        );
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      hideOnClick: true,
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-slate-11"
  >
    <div class="flex flex-col items-end">
      <span>{{ `${createdAtTime} • ${lastClientMessageTime}` }}</span>
      <span
        v-if="lastClientMessageActualTime"
        class="text-xs font-medium text-n-slate-10"
      >
        {{ lastClientMessageActualTime }}
      </span>
    </div>
  </div>
</template>
